import React from 'react';
import { Box, Typography, FormLabel, Grid } from '@material-ui/core';
import getLeadPrice from 'config/helpers/leadPricing';

// Helper: get upcoming Saturday (start of billing week) and corresponding billing Monday
function getUpcomingSaturday(fromDate = new Date()) {
    const date = new Date(fromDate);
    const day = date.getDay(); // 0 Sun ... 6 Sat
    const daysUntilSaturday = (6 - day + 7) % 7; // if already Saturday => 0
    date.setDate(date.getDate() + daysUntilSaturday);
    date.setHours(0,0,0,0);
    return date;
}

// Helper: get the Saturday at or before the given date (current delivery week start)
function getCurrentWeekSaturday(fromDate = new Date()) {
    const date = new Date(fromDate);
    const day = date.getDay(); // 0 Sun ... 6 Sat
    const daysSinceSaturday = (day - 6 + 7) % 7; // 0 if Saturday
    date.setDate(date.getDate() - daysSinceSaturday);
    date.setHours(0,0,0,0);
    return date;
}

function getWeekEnd(startSaturday) {
    const friday = new Date(startSaturday);
    // Friday is 6 days after Saturday
    friday.setDate(friday.getDate() + 6);
    friday.setHours(23,59,59,999);
    return friday;
}

function getBillingMonday(forFriday) {
    const monday = new Date(forFriday);
    // move to next day until Monday (1)
    do {
        monday.setDate(monday.getDate() + 1);
    } while (monday.getDay() !== 1);
    monday.setHours(9,0,0,0); // charge time 09:00 illustrative
    return monday;
}

// Dynamic price per lead: prefer admin-set companyDetails.planDetails.leadPrice,
// fall back to computed value from coverage type + county count.
function resolveLeadPrice(companyDetails) {
    const companyLeadPrice = companyDetails.planDetails && companyDetails.planDetails.leadPrice;
    if (companyLeadPrice != null && companyLeadPrice !== '') {
        return Number(companyLeadPrice);
    }
    const countyCount = (companyDetails.counties || []).length;
    return getLeadPrice(companyDetails.coverageType, countyCount);
}

function getMonthlyPeriod(now, planActive) {
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day current month
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const followingMonthStart = new Date(now.getFullYear(), now.getMonth() + 2, 1);

    if (planActive) {
        return {
            deliveryStart: currentMonthStart,
            deliveryEnd: currentMonthEnd,
            billingDate: nextMonthStart,
            labelDelivery: 'Current Delivery Month',
            labelBilling: 'Next Billing Date'
        }
    }
    // first activation begins next full month
    return {
        deliveryStart: nextMonthStart,
        deliveryEnd: nextMonthEnd,
        billingDate: followingMonthStart,
        labelDelivery: 'First Delivery Month',
        labelBilling: 'First Billing Date'
    }
}

const DeliverySummaryCard = ({ companyDetails, selectedLeads, planActive=false, topupTotalLeads, remainingBalance }) => {
    const now = new Date();
    const billingPeriod = (companyDetails.billingPeriod || 'weekly').toLowerCase();
    const planType = (companyDetails.planType || 'weekly').toLowerCase();
    const isTopup = planType === 'topup';

    // Weekly calculations
    const deliveryWeekStart = planActive ? getCurrentWeekSaturday(now) : getUpcomingSaturday(now);
    const deliveryWeekEnd = getWeekEnd(deliveryWeekStart);
    const billingMonday = getBillingMonday(deliveryWeekEnd);

    // Monthly calculations
    const monthly = getMonthlyPeriod(now, planActive);

    const leadPrice = resolveLeadPrice(companyDetails);

    return (
        <Box>
            <Typography variant="h3">
                {isTopup
                    ? `Topup - Max ${selectedLeads} leads per week`
                    : `${companyDetails.name} - ${selectedLeads} leads per ${billingPeriod === 'monthly' ? 'month' : 'week'}`}
            </Typography>
            {isTopup ? (
                <Typography variant="body2">
                    You will receive up to {selectedLeads} leads per week (Saturday – Friday). This is a maximum cap, not a guarantee. Leads will continue to be delivered until your balance reaches €0.
                </Typography>
            ) : billingPeriod === 'weekly' ? (
                <Typography variant="body2">
                    You will receive up to {selectedLeads} leads per week (Saturday – Friday). This is a maximum cap, not a guarantee. You are billed weekly in arrears on Monday for the prior week’s delivered leads.
                </Typography>
            ) : (
                <Typography variant="body2">
                    You will receive up to {selectedLeads} leads per month. This is a maximum cap, not a guarantee. You are billed monthly in arrears on the 1st of each month for the prior month’s delivered leads.
                </Typography>
            )}
            <Box mt={3}>
                <Box>
                    <FormLabel>Counties</FormLabel>
                    {companyDetails.counties && companyDetails.counties.length > 0 ? (
                        <Typography variant="body2">
                            {companyDetails.counties.join(', ')}
                        </Typography>
                    ) : (
                        <Typography 
                            variant="body2" 
                            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#229626' }}
                            onClick={() => window.location.assign('/profile')}
                            onKeyDown={(e) => { if (e.key === 'Enter') window.location.assign('/profile') }}
                            role='button'
                            tabIndex={0}
                        >
                            Configure your counties on the profile page
                        </Typography>
                    )}
                </Box>
                {isTopup ? null : billingPeriod === 'weekly' ? (
                    <>
                        <Box mt={2}>
                            <FormLabel>{planActive ? 'Current Delivery Week' : 'First Delivery Week'}</FormLabel>
                            <Typography variant="body2" color="#8898aa">
                                {deliveryWeekStart.toLocaleDateString()} – {deliveryWeekEnd.toLocaleDateString()}
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <FormLabel>{planActive ? 'Next Billing Date' : 'First Billing Date'}</FormLabel>
                            <Typography variant="body2" color="#8898aa">
                                {billingMonday.toLocaleDateString()} (weekly in arrears)
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box mt={2}>
                            <FormLabel>{monthly.labelDelivery}</FormLabel>
                            <Typography variant="body2" color="#8898aa">
                                {monthly.deliveryStart.toLocaleDateString()} – {monthly.deliveryEnd.toLocaleDateString()}
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <FormLabel>{monthly.labelBilling}</FormLabel>
                            <Typography variant="body2" color="#8898aa">
                                {monthly.billingDate.toLocaleDateString()} (monthly in arrears)
                            </Typography>
                        </Box>
                    </>
                )}
                {isTopup ? (
                    <Box mt={2}>
                        <FormLabel>Remaining Balance</FormLabel>
                        <Typography variant="body2" color="#8898aa">
                            {remainingBalance != null && !isNaN(remainingBalance) ? `€${Number(remainingBalance).toFixed(2)}` : '—'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box mt={2}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <FormLabel>{`Max ${billingPeriod === 'monthly' ? 'Monthly' : 'Weekly'} Subtotal`}</FormLabel>
                                </Grid>
                                <Grid item xs={6} style={{ textAlign: 'right' }}>
                                    <Typography variant="body2" color="#8898aa">
                                        €{selectedLeads ? (selectedLeads * leadPrice).toFixed(2) : '0.00'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box mt={2}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <FormLabel>{`Max ${billingPeriod === 'monthly' ? 'Monthly' : 'Weekly'} VAT (@23%)`}</FormLabel>
                                </Grid>
                                <Grid item xs={6} style={{ textAlign: 'right' }}>
                                    <Typography variant="body2" color="#8898aa">
                                        €{selectedLeads ? ((selectedLeads * leadPrice) * 0.23).toFixed(2) : '0.00'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box mt={2}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <FormLabel>{`Max ${billingPeriod === 'monthly' ? 'Monthly' : 'Weekly'} Total`}</FormLabel>
                                </Grid>
                                <Grid item xs={6} style={{ textAlign: 'right' }}>
                                    <Typography variant="body2" color="#8898aa">
                                        €{selectedLeads ? ((selectedLeads * leadPrice) * 1.23).toFixed(2) : '0.00'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default DeliverySummaryCard;